import { getToken, onMessage } from 'firebase/messaging';
import { getMessagingIfSupported, resetMessagingInstance, VAPID_KEY } from '../../firebase';
import { firebaseConfig, getFirebaseConfigSummary } from '../../config/firebaseConfig';
import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

let isRegistering = false;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isAuthError = (error) =>
  error?.code === 'messaging/token-subscribe-failed' ||
  error?.message?.includes('UNAUTHENTICATED') ||
  error?.message?.includes('authentication credential');

const clearFirebaseInstallations = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if (typeof indexedDB.databases === 'function') {
      const databases = await indexedDB.databases();
      await Promise.all(
        databases
          .filter((db) => db.name?.includes('firebase'))
          .map((db) => indexedDB.deleteDatabase(db.name))
      );
    }
  } catch {
    // Best-effort cleanup only
  }

  resetMessagingInstance();
  localStorage.removeItem('last_fcm_token');
};

const getFcmTokenWithRetry = async (messaging, swReg, maxAttempts = 3) => {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });
    } catch (error) {
      lastError = error;

      if (!isAuthError(error) || attempt === maxAttempts) {
        throw error;
      }

      console.warn(
        `[FCM] getToken attempt ${attempt} failed, clearing stale Firebase state and retrying...`
      );
      await clearFirebaseInstallations();
      await sleep(1500 * attempt);
    }
  }

  throw lastError;
};

/**
 * Request notification permission, get FCM token, and register it with the backend.
 * Call this once after a successful login.
 */
export const registerFcmToken = async () => {
  if (isRegistering) {
    console.log('[FCM] Registration already in progress...');
    return null;
  }

  try {
    isRegistering = true;

    const messaging = await getMessagingIfSupported();
    if (!messaging) {
      if (!firebaseConfig.appId) {
        console.error(
          '[FCM] Web appId is missing. The config you shared is Android-only. ' +
            'In Firebase Console → tenantscore-86873 → Project Settings → Your apps → ' +
            'Add app → Web, then copy appId into src/config/firebase.config.json'
        );
      } else {
        console.log('[FCM] Firebase messaging not supported in this browser.');
      }
      isRegistering = false;
      return null;
    }

    if (!('Notification' in window)) {
      console.log('[FCM] Notifications not supported in this browser.');
      isRegistering = false;
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[FCM] Notification permission denied.');
      isRegistering = false;
      return null;
    }

    const lastVapid = localStorage.getItem('last_fcm_vapid');
    if (lastVapid !== VAPID_KEY) {
      await clearFirebaseInstallations();
      localStorage.setItem('last_fcm_vapid', VAPID_KEY);
    }

    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await swReg.update();

    await new Promise((resolve) => {
      if (swReg.active) {
        resolve();
      } else {
        const sw = swReg.installing || swReg.waiting;
        if (sw) {
          sw.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') resolve();
          });
        } else {
          navigator.serviceWorker.ready.then(() => resolve());
        }
      }
    });

    const token = await getFcmTokenWithRetry(messaging, swReg);

    if (!token) {
      console.log('[FCM] No token received. Check VAPID key and Firebase config.');
      isRegistering = false;
      return null;
    }

    const lastToken = localStorage.getItem('last_fcm_token');
    if (lastToken === token) {
      console.log('[FCM] Token already registered with backend.');
      isRegistering = false;
      return token;
    }

    let deviceId = localStorage.getItem('fcm_device_id');
    if (!deviceId) {
      deviceId = `web-${Math.random().toString(36).substring(2, 11)}-${Date.now()}`;
      localStorage.setItem('fcm_device_id', deviceId);
    }

    console.log('[FCM] Device token:', token);

    const response = await apiClient(
      `${API_BASE_URL}/api/notifications/fcm-token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          platform: 'WEB',
          deviceId,
        }),
      },
      true
    );

    if (response.ok) {
      console.log('[FCM] Token registered with backend successfully.');
      localStorage.setItem('last_fcm_token', token);
    } else {
      console.warn('[FCM] Backend token registration failed:', response.status);
    }

    isRegistering = false;
    return token;
  } catch (error) {
    if (isAuthError(error)) {
      console.error(
        `[FCM] Firebase auth failed for project "${firebaseConfig.projectId}". ` +
          'The web app config (apiKey, appId) and VAPID key must all come from the same Firebase project. ' +
          'Open Firebase Console → Project Settings → Your apps → Web app and copy the full firebaseConfig object.',
        getFirebaseConfigSummary(),
        error
      );
    } else {
      console.error('[FCM] Error registering FCM token:', error);
    }

    isRegistering = false;
    return null;
  }
};

/**
 * Listen for foreground push messages (when the app tab is open/focused).
 * Pass a callback to handle the notification payload.
 */
export const onForegroundMessage = (callback) => {
  let unsubscribe = () => {};

  getMessagingIfSupported()
    .then((messaging) => {
      if (!messaging) {
        console.log('[FCM] Foreground listener skipped: messaging unsupported.');
        return;
      }

      unsubscribe = onMessage(messaging, (payload) => {
        console.log('[FCM] Foreground message:', payload);
        if (callback) callback(payload);
      });
    })
    .catch((error) => {
      console.warn('[FCM] Failed to set foreground listener:', error);
    });

  return () => unsubscribe();
};
